import { useQuery } from '@tanstack/react-query';
import { ApiError, api, type Me } from '../api/client.js';

/**
 * The current user, or null.
 *
 * Derived from /auth/me rather than from anything stored client-side: the
 * session cookie is HttpOnly and unreadable from JavaScript, so the server is
 * the only thing that knows whether we are signed in. That is deliberate — it
 * means a stale client cannot believe it is authenticated when it is not.
 */
export function useAuth() {
  const query = useQuery({
    queryKey: ['me'],
    queryFn: async (): Promise<Me | null> => {
      try {
        return await api.get<Me>('/auth/me');
      } catch (err) {
        // 401 is the expected answer for "not signed in", not a failure.
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    can: (permission: string) => query.data?.permissions.includes(permission) ?? false,
  };
}
